import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '../../constants/routePaths';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Button,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Circle as CircleIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { toast } from 'sonner';

import { deploymentInstanceService } from '../../services/DeploymentInstanceService';


export interface DeployedInstance {
  instance_id: string;
  instance_name: string;
  instance_status: string;
  model_name: string;
}

export function DeploymentsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deploymentToDelete, setDeploymentToDelete] = useState<DeployedInstance | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deployedInstances, setDeployedInstances] = useState<DeployedInstance[]>([]);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, deploymentId: string) => {
    setAnchorEl({ ...anchorEl, [deploymentId]: event.currentTarget });
  };

  const handleMenuClose = (deploymentId: string) => {
    setAnchorEl({ ...anchorEl, [deploymentId]: null });
  };

  const handleDeleteClick = (deployment: DeployedInstance) => {
    setDeploymentToDelete(deployment);
    setDeleteDialogOpen(true);
    handleMenuClose(deployment.instance_id);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeploymentToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deploymentToDelete) return;
    setIsDeleting(true);
    // Simulate API call
    try {
      const newModel = await deploymentInstanceService.deleteDeploymentInstanceById(deploymentToDelete.instance_id, deploymentToDelete.model_name);
      console.log('API response:', newModel);
      // In a real app, you would call your API here
      // await deleteDeploymentAPI(deploymentToDelete.id); 
      const data = deployedInstances.filter(deployment => deployment.instance_id !== deploymentToDelete.instance_id);
      setDeployedInstances(data); 
      toast.success(`Deployment "${deploymentToDelete.instance_name}" deleted successfully`, {
        duration: 4000,
      });
      
      setDeleteDialogOpen(false);
      setDeploymentToDelete(null);
    } catch (error) {
      toast.error('Failed to delete deployment', {
        duration: 4000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

    const fetchData = async () => {
      try {
        const newModel = await deploymentInstanceService.getDeploymentInstances();
        setDeployedInstances(newModel);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error('Error fetching deployment instances:', error);
      }
    };
    

  const filteredDeployments = deployedInstances.filter(deployment =>
    deployment.instance_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    deployment.model_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusIcon = (status: DeployedInstance['instance_status']) => {
    switch (status) {
      case 'Running':
        return <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />;
      case 'Stopped':
        return <CircleIcon sx={{ fontSize: 18, color: '#64748b' }} />;
      case 'Deploying':
        return <PendingIcon sx={{ fontSize: 18, color: '#f59e0b' }} />;
      case 'Error':
        return <ErrorIcon sx={{ fontSize: 18, color: '#ef4444' }} />;
    }
  };

  const getStatusColor = (status: DeployedInstance['instance_status']) => {
    switch (status) {
      case 'Running':
        return { bg: '#dcfce7', text: '#166534', border: '#86efac' };
      case 'Stopped':
        return { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
      case 'Deploying':
        return { bg: '#fef3c7', text: '#92400e', border: '#fde047' };
      case 'Error':
        return { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' };
    }
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: 'calc(100vh - 64px)' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                Deployed Instances
              </Typography>
            </Box>
            
          </Box>

          {/* Enhanced Search */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              bgcolor: 'white',
              transition: 'all 0.2s ease-in-out',
              '&:focus-within': {
                borderColor: '#667eea',
                boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SearchIcon sx={{ color: '#94a3b8', fontSize: 24 }} />
              <TextField
                fullWidth
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: '15px',
                    '& input::placeholder': {
                      color: '#94a3b8',
                      opacity: 1
                    }
                  }
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    padding: 0
                  }
                }}
              />
              {searchQuery && (
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery('')}
                  sx={{
                    color: '#94a3b8',
                    '&:hover': {
                      bgcolor: '#f1f5f9',
                      color: '#64748b'
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            
            {searchQuery && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #f1f5f9' }}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Found {filteredDeployments.length} {filteredDeployments.length === 1 ? 'deployment' : 'deployments'}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>

        {isLoading ? (
          <Paper
            elevation={0}
            sx={{
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              p: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3
            }}
          >
            <CircularProgress size={48} sx={{ color: '#667eea' }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a', mb: 1 }}>
                Loading Deployments
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Please wait while we fetch your deployed instances...
              </Typography>
            </Box>
          </Paper>
        ) : (
          <>
        {/* Deployments Table */}
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Deployment Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Model</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDeployments.map((deployment) => {
                const statusColors = getStatusColor(deployment.instance_status);
                
                return (
                  <TableRow
                    key={deployment.instance_id}
                    onClick={() => navigate(ROUTE_PATHS.DEPLOYMENT_DETAIL(deployment.instance_id))}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#f8fafc'
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                        {deployment.instance_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {deployment.model_name}
                      </Typography>
                      {/* <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        {deployment.modelVersion}
                      </Typography> */}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(deployment.instance_status)}
                        label={deployment.instance_status}
                        size="small"
                        sx={{
                          bgcolor: statusColors?.bg,
                          color: statusColors?.text,
                          border: `1px solid ${statusColors?.border}`,
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}
                      />
                    </TableCell>
                   
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, deployment.instance_id);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl[deployment.instance_id]}
                        open={Boolean(anchorEl[deployment.instance_id])}
                        onClose={() => handleMenuClose(deployment.instance_id)}
                        onClick={(e) => e.stopPropagation()}
                        PaperProps={{
                          elevation: 3,
                          sx: { borderRadius: 2, minWidth: 160 }
                        }}
                      >
                        
                        <MenuItem onClick={() => handleDeleteClick(deployment)} sx={{ color: '#ef4444' }}>
                          <ListItemIcon>
                            <DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} />
                          </ListItemIcon>
                          <ListItemText>Delete</ListItemText>
                        </MenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredDeployments.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
              No deployments found
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Try adjusting your search query
            </Typography>
          </Box>
          )}
          </>
        )}
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={isDeleting ? undefined : handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '1px solid #e2e8f0'
          }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Delete Deployment
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#64748b', mb: 2 }}>
            Are you sure you want to delete the deployment <strong style={{ color: '#0f172a' }}>"{deploymentToDelete?.instance_name}"</strong>?
          </DialogContentText>
          <DialogContentText sx={{ color: '#64748b' }}>
            This action cannot be undone. The deployment will be permanently removed from your account.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={isDeleting}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': {
                borderColor: '#cbd5e1',
                bgcolor: '#f8fafc'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
            variant="contained"
            startIcon={isDeleting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DeleteIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#ef4444',
              '&:hover': {
                bgcolor: '#dc2626'
              },
              '&:disabled': {
                bgcolor: '#fca5a5',
                color: 'white'
              }
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
}
